import React, { useEffect, useRef, useState } from "react";
import { useLeadSocket, type LeadEventName } from "../SocketProvider.hooks";
import {
  Alert,
  IconButton,
  Snackbar,
  Box,
  Typography,
} from "@mui/material";
import { Close, PersonAdd } from "@mui/icons-material";
import { keyframes } from "@mui/system";

const slideIn = keyframes`
  from {
    transform: translateY(100%);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const pulseAnimation = keyframes`
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.02);
  }
  100% {
    transform: scale(1);
  }
`;

const getLeadTitle = (eventName: LeadEventName | null): string => {
  switch (eventName) {
    case "GOOGLE_LEAD":
      return "New Google lead detected";
    case "EXOTEL_LEAD":
      return "New Exotel lead detected";
    case "CAMPAIGNLEAD":
      return "New campaign lead detected";
    case "upcomingFollowupLead":
      return "Upcoming followup lead detected";
    default:
      return "New lead detected";
  }
};

const getAlertColors = (eventName: LeadEventName | null) => {
  const schemes: Record<
    LeadEventName | "default",
    { background: string; boxShadow: string; boxShadowHover: string }
  > = {
    GOOGLE_LEAD: {
      background: "linear-gradient(135deg, #4285F4 0%, #34A853 100%)",
      boxShadow: "0 8px 32px rgba(66, 133, 244, 0.3)",
      boxShadowHover: "0 8px 32px rgba(66, 133, 244, 0.4)",
    },
    EXOTEL_LEAD: {
      background: "linear-gradient(135deg, #00897B 0%, #00695C 100%)",
      boxShadow: "0 8px 32px rgba(0, 137, 123, 0.3)",
      boxShadowHover: "0 8px 32px rgba(0, 137, 123, 0.4)",
    },
    CAMPAIGNLEAD: {
      background: "linear-gradient(135deg, #7B1FA2 0%, #512DA8 100%)",
      boxShadow: "0 8px 32px rgba(123, 31, 162, 0.3)",
      boxShadowHover: "0 8px 32px rgba(123, 31, 162, 0.4)",
    },
    upcomingFollowupLead: {
      background: "linear-gradient(135deg, #FF9800 0%, #F57C00 100%)",
      boxShadow: "0 8px 32px rgba(255, 152, 0, 0.3)",
      boxShadowHover: "0 8px 32px rgba(255, 152, 0, 0.4)",
    },
    default: {
      background: "linear-gradient(135deg, #2196F3 0%, #1976D2 100%)",
      boxShadow: "0 8px 32px rgba(33, 150, 243, 0.25)",
      boxShadowHover: "0 8px 32px rgba(33, 150, 243, 0.35)",
    },
  };
  const key = eventName && eventName in schemes ? eventName : "default";
  return schemes[key];
};

const ALARM_AUDIO_SRC = "/alarm-tone.wav";
const UPCOMING_FOLLOWUP_LEAD_AUDIO_SRC = "/upcoming-followup-lead-tone.mp3";

export const AlertSnackbar = () => {
  const leadAlert = useLeadSocket();
  const [openSnackBar, setOpenSnackBar] = useState(false);
  const [displayedAlert, setDisplayedAlert] = useState<typeof leadAlert>(null);
  const alarmAudioRef = useRef<HTMLAudioElement | null>(null);

  const stopAlarm = () => {
    const audio = alarmAudioRef.current;
    if (audio) {
      audio.pause();
      audio.currentTime = 0;
    }
  };

  const playAlarm = () => {
    console.log("🔊 Playing Lead Alarm for:", displayedAlert?.eventName);
    if (alarmAudioRef.current) {
        stopAlarm();
    }
    alarmAudioRef.current = new Audio(displayedAlert?.eventName === "upcomingFollowupLead" ? UPCOMING_FOLLOWUP_LEAD_AUDIO_SRC : ALARM_AUDIO_SRC);
    const audio = alarmAudioRef.current;
    audio.currentTime = 0;
    audio.play().catch((err) => {
      console.warn("🔊 Autoplay blocked or audio error:", err);
    });
  };

  useEffect(() => {
    if (leadAlert) {
      console.log("📢 Lead Alert Received in Snackbar Component:", leadAlert);
      setOpenSnackBar(false); // Quick toggle to ensure effect re-triggers if same type
      setTimeout(() => {
        setDisplayedAlert(leadAlert);
        setOpenSnackBar(true);
      }, 50);
    }
  }, [leadAlert]);

  useEffect(() => {
    if (openSnackBar) {
      playAlarm();
      return () => stopAlarm();
    }
  }, [openSnackBar]);

  useEffect(() => {
    return () => stopAlarm();
  }, []);

  const handleClose = (
    _event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway" || reason === "escapeKeyDown") return;
    stopAlarm();
    setOpenSnackBar(false);
    setDisplayedAlert(null);
  };

  if (!openSnackBar || !displayedAlert) return <></>;

  const title = getLeadTitle(displayedAlert.eventName);
  const contactNumber = displayedAlert.contact_number;
  const alertColors = getAlertColors(displayedAlert.eventName);

  return (
    <Snackbar
      open={openSnackBar}
      autoHideDuration={null}
      onClose={handleClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      sx={{
        width: { xs: "90%", sm: "400px" },
        p: 1,
        mb: 3,
        mr: 3,
      }}
    >
      <Alert
        severity="info"
        variant="filled"
        onClose={handleClose}
        icon={
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 44,
              height: 44,
              borderRadius: "50%",
              background:
                "linear-gradient(135deg, rgba(255,255,255,0.3), rgba(255,255,255,0.1))",
              backdropFilter: "blur(4px)",
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "scale(1.05)",
                background:
                  "linear-gradient(135deg, rgba(255,255,255,0.4), rgba(255,255,255,0.2))",
              },
            }}
          >
            <PersonAdd sx={{ fontSize: 26, color: "#fff" }} />
          </Box>
        }
        sx={{
          width: "100%",
          py: 2.5,
          px: 2,
          ...alertColors,
          backdropFilter: "blur(10px)",
          borderRadius: "16px",
          animation: `${slideIn} 0.5s ease-out, ${pulseAnimation} 2s ease-in-out infinite`,
          border: "1px solid rgba(255, 255, 255, 0.2)",
          transition: "all 0.3s ease",
          "&:hover": {
            background: alertColors.background,
            boxShadow: alertColors.boxShadowHover,
            borderColor: "rgba(255, 255, 255, 0.3)",
          },
          "& .MuiAlert-message": {
            flex: 1,
            display: "flex",
            alignItems: "center",
          },
          "& .MuiAlert-icon": {
            padding: 0,
            marginRight: 2,
          },
        }}
        action={
          <IconButton
            onClick={handleClose}
            color="inherit"
            sx={{
              backgroundColor: "rgba(255, 255, 255, 0.1)",
              backdropFilter: "blur(4px)",
              "&:hover": {
                backgroundColor: "rgba(255, 255, 255, 0.2)",
                transform: "rotate(90deg)",
              },
              transition: "all 0.3s ease-in-out",
            }}
          >
            <Close sx={{ color: "#fff" }} />
          </IconButton>
        }
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 600,
              letterSpacing: 0.5,
              textShadow: "0 2px 4px rgba(0,0,0,0.1)",
              color: "#fff",
              fontSize: "1rem",
              lineHeight: 1.5,
            }}
          >
            {title}
          </Typography>
          {contactNumber && (
            <Typography
              variant="body2"
              sx={{
                color: "rgba(255, 255, 255, 0.95)",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              Contact number: {contactNumber}
            </Typography>
          )}
        </Box>
      </Alert>
    </Snackbar>
  );
};
